import {useAuth} from '@apps-common/auth-react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Box, Button, Container, Stack, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {showReportDialog} from '@sentry/react';

import PropTypes from 'prop-types';
import React from 'react';
import BirdnestSatelliteIcon from '@app/assets/images/birdnest-in-your-antenna.jsx';

const PlanetSupportButton = () => {
  return (
    <Button
      color="primary"
      variant="contained"
      href="https://support.planet.com/hc/en-us/requests/new"
      target="_blank"
      rel="noreferrer noopener"
      endIcon={<OpenInNewIcon fontSize="small" />}
    >
      Report Issue
    </Button>
  );
};

const SentryReportButton = ({eventId}) => {
  const {user} = useAuth();

  return (
    <Button
      color="primary"
      variant="contained"
      onClick={() =>
        showReportDialog({
          eventId: eventId,
          user: {
            name: user?.claims.name,
            email: user?.claims.email,
          },
        })
      }
    >
      Report Issue
    </Button>
  );
};

SentryReportButton.propTypes = {
  eventId: PropTypes.string.isRequired,
};

const ErrorPage = ({
  statusCode,
  message,
  showSupportButton = true,
  ...props
}) => {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="md">
        <Grid container spacing={4}>
          <Grid
            size={{
              xs: 'grow',
              sm: 3,
            }}
            sx={{
              display: {xs: 'none', sm: 'flex'},
            }}
          >
            <BirdnestSatelliteIcon width="100%" />
          </Grid>
          <Grid
            container
            size={{
              xs: 12,
              sm: 'grow',
            }}
          >
            <Grid container direction="column" spacing={2} size="grow">
              <Grid size="grow">
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: '100%',
                    justifyContent: 'center',
                  }}
                >
                  {statusCode && (
                    <Typography
                      variant="h2"
                      align="center"
                      noWrap
                      sx={{
                        color: 'grey.500',
                        marginBottom: 2,
                      }}
                    >
                      {statusCode}
                    </Typography>
                  )}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      marginBottom: 2,
                    }}
                  >
                    Houston, we have a problem...
                  </Typography>

                  <Typography gutterBottom sx={{color: 'text.secondary'}}>
                    {message || 'An error occurred, that we could not foresee.'}{' '}
                    We’re sorry about that!
                  </Typography>
                  <Typography gutterBottom sx={{color: 'text.secondary'}}>
                    {statusCode === 404
                      ? 'Let us know if you think there is a problem on our side.'
                      : 'Try refreshing the page or let us know what happened, we are continuously working to improve your experience.'}
                  </Typography>
                </Box>
              </Grid>

              <Grid>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    justifyContent: 'flex-end',
                  }}
                >
                  {props.onRetry && (
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={props.onRetry}
                      startIcon={<RefreshIcon />}
                    >
                      {props.retryMessage || 'Refresh'}
                    </Button>
                  )}
                  {props.sentryEventId && (
                    <SentryReportButton eventId={props.sentryEventId} />
                  )}
                  {!props.sentryEventId && showSupportButton && (
                    <PlanetSupportButton />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

ErrorPage.propTypes = {
  statusCode: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  message: PropTypes.string,
  sentryEventId: PropTypes.string,
  authUser: PropTypes.object,
  showSupportButton: PropTypes.bool,
  onRetry: PropTypes.func,
  retryMessage: PropTypes.string,
};

export default ErrorPage;
